import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { 
  useLocalStorage, 
  STORAGE_KEYS, 
  createInitialNoteStats,
  createInitialIntervalStats,
  DEFAULT_SETTINGS,
  DEFAULT_STREAK,
  DEFAULT_RANGE,
  DEFAULT_INTERVAL_STATE,
  INTERVALS,
  getMasteryTier,
  getNoteFrequency,
  getNextNoteToUnlock,
  checkStreak,
  MASTERY_CONFIG
} from '../hooks/useLocalStorage';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // Persistent state
  const [notes, setNotes] = useLocalStorage(STORAGE_KEYS.NOTES, createInitialNoteStats());
  const [intervals, setIntervals] = useLocalStorage(STORAGE_KEYS.INTERVALS, DEFAULT_INTERVAL_STATE);
  const [sessions, setSessions] = useLocalStorage(STORAGE_KEYS.SESSIONS, []);
  const [streak, setStreak] = useLocalStorage(STORAGE_KEYS.STREAK, DEFAULT_STREAK);
  const [settings, setSettings] = useLocalStorage(STORAGE_KEYS.SETTINGS, DEFAULT_SETTINGS);
  const [currentRange, setCurrentRange] = useLocalStorage(STORAGE_KEYS.CURRENT_RANGE, DEFAULT_RANGE);
  const [onboardingComplete, setOnboardingComplete] = useLocalStorage(STORAGE_KEYS.ONBOARDING, false);

  // Count total mastered notes
  const getMasteredCount = useCallback(() => {
    return currentRange.unlockedNotes.filter(note => {
      const stats = notes[note];
      return stats && getMasteryTier(stats.correctCount, stats.incorrectCount) >= 3;
    }).length;
  }, [notes, currentRange.unlockedNotes]);

  // Update note statistics after an answer - returns mastery info
  const updateNoteStats = useCallback((note, isCorrect, responseTime) => {
    // Get current stats from the notes state
    const currentStats = notes[note] || createInitialNoteStats([note])[note];
    const previousTier = getMasteryTier(currentStats.correctCount, currentStats.incorrectCount);
    
    // Calculate what the new stats will be
    const newCorrectCount = isCorrect ? currentStats.correctCount + 1 : currentStats.correctCount;
    const newIncorrectCount = isCorrect ? currentStats.incorrectCount : currentStats.incorrectCount + 1;
    const newTier = getMasteryTier(newCorrectCount, newIncorrectCount);
    
    // Check if this will trigger mastery
    const newlyMastered = previousTier < 3 && newTier >= 3;
    
    // Update the state - use functional update to ensure we get latest state
    setNotes(prevNotes => {
      const noteStats = prevNotes[note] || createInitialNoteStats([note])[note];
      
      const newResponseTimes = [...(noteStats.responseTimes || []), responseTime].slice(-10);
      
      const updatedStats = {
        ...noteStats,
        correctCount: isCorrect ? noteStats.correctCount + 1 : noteStats.correctCount,
        incorrectCount: isCorrect ? noteStats.incorrectCount : noteStats.incorrectCount + 1,
        lastShownTimestamp: Date.now(),
        responseTimes: newResponseTimes,
        // Always recalculate mastery tier to ensure it's correct
        masteryTier: getMasteryTier(
          isCorrect ? noteStats.correctCount + 1 : noteStats.correctCount,
          isCorrect ? noteStats.incorrectCount : noteStats.incorrectCount + 1
        ),
        currentFrequency: getNoteFrequency({
          correctCount: isCorrect ? noteStats.correctCount + 1 : noteStats.correctCount,
          incorrectCount: isCorrect ? noteStats.incorrectCount : noteStats.incorrectCount + 1
        })
      };
      
      return {
        ...prevNotes,
        [note]: updatedStats
      };
    });
    
    return { newlyMastered, previousTier };
  }, [notes, setNotes]);

  // Unlock a single new note when a note is mastered
  const unlockNewNote = useCallback(() => {
    const nextNote = getNextNoteToUnlock(currentRange, notes);
    
    if (nextNote) {
      // Initialize stats for new note
      setNotes(prev => ({
        ...prev,
        ...createInitialNoteStats([nextNote.note])
      }));
      
      // Update range
      const allUnlocked = [...currentRange.unlockedNotes, nextNote.note].sort((a, b) => {
        const noteOrder = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
        const aOctave = parseInt(a.slice(-1));
        const bOctave = parseInt(b.slice(-1));
        if (aOctave !== bOctave) return aOctave - bOctave;
        return noteOrder.indexOf(a[0]) - noteOrder.indexOf(b[0]);
      });
      
      setCurrentRange({
        min: allUnlocked[0],
        max: allUnlocked[allUnlocked.length - 1],
        unlockedNotes: allUnlocked
      });
      
      return nextNote;
    }
    return null;
  }, [notes, currentRange, setNotes, setCurrentRange]);

  // Save a completed session
  const saveSession = useCallback((sessionData) => {
    const session = {
      ...sessionData,
      timestamp: Date.now(),
      id: `session_${Date.now()}`
    };
    
    setSessions(prev => [...prev.slice(-99), session]); // Keep last 100 sessions
    
    // Update streak
    const streakResult = checkStreak(streak);
    setStreak({
      currentCount: streakResult.currentCount,
      lastSessionDate: streakResult.lastSessionDate
    });
    
    return streakResult;
  }, [setSessions, streak, setStreak]);

  // Check if range should expand — delegates to pedagogy sequence
  const checkRangeExpansion = useCallback(() => {
    const nextNote = getNextNoteToUnlock(currentRange, notes);
    if (nextNote) {
      setNotes(prev => ({
        ...prev,
        ...createInitialNoteStats([nextNote.note])
      }));
      const noteOrder = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
      const allUnlocked = [...currentRange.unlockedNotes, nextNote.note].sort((a, b) => {
        const aOctave = parseInt(a.slice(-1));
        const bOctave = parseInt(b.slice(-1));
        if (aOctave !== bOctave) return aOctave - bOctave;
        return noteOrder.indexOf(a[0]) - noteOrder.indexOf(b[0]);
      });
      setCurrentRange({
        min: allUnlocked[0],
        max: allUnlocked[allUnlocked.length - 1],
        unlockedNotes: allUnlocked
      });
      return [nextNote.note];
    }
    return null;
  }, [notes, currentRange, setNotes, setCurrentRange]);

  // Update settings
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }, [setSettings]);

  // Complete onboarding
  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(true);
  }, [setOnboardingComplete]);

  // Update interval statistics after an answer
  const updateIntervalStats = useCallback((interval, isCorrect, responseTime) => {
    const currentStats = intervals[interval] || createInitialIntervalStats([interval])[interval];
    const previousTier = getMasteryTier(currentStats.correctCount, currentStats.incorrectCount);
    
    const newCorrectCount = isCorrect ? currentStats.correctCount + 1 : currentStats.correctCount;
    const newIncorrectCount = isCorrect ? currentStats.incorrectCount : currentStats.incorrectCount + 1;
    const newTier = getMasteryTier(newCorrectCount, newIncorrectCount);
    
    const newlyMastered = previousTier < 3 && newTier >= 3;
    
    setIntervals(prevIntervals => {
      const intervalStats = prevIntervals[interval] || createInitialIntervalStats([interval])[interval];
      
      const newResponseTimes = [...(intervalStats.responseTimes || []), responseTime].slice(-10);
      
      const updatedStats = {
        ...intervalStats,
        correctCount: isCorrect ? intervalStats.correctCount + 1 : intervalStats.correctCount,
        incorrectCount: isCorrect ? intervalStats.incorrectCount : intervalStats.incorrectCount + 1,
        lastShownTimestamp: Date.now(),
        responseTimes: newResponseTimes,
        masteryTier: getMasteryTier(
          isCorrect ? intervalStats.correctCount + 1 : intervalStats.correctCount,
          isCorrect ? intervalStats.incorrectCount : intervalStats.incorrectCount + 1
        ),
        currentFrequency: getNoteFrequency({
          correctCount: isCorrect ? intervalStats.correctCount + 1 : intervalStats.correctCount,
          incorrectCount: isCorrect ? intervalStats.incorrectCount : intervalStats.incorrectCount + 1
        })
      };
      
      return {
        ...prevIntervals,
        [interval]: updatedStats
      };
    });
    
    return { newlyMastered, previousTier };
  }, [intervals, setIntervals]);

  // Get interval statistics summary
  const getIntervalStatsSummary = useMemo(() => {
    const totalIntervals = INTERVALS.length;
    let masteredCount = 0;
    let totalCorrect = 0;
    let totalIncorrect = 0;
    
    Object.keys(intervals).forEach(interval => {
      const stats = intervals[interval];
      if (stats) {
        if (stats.masteryTier >= 3) masteredCount++;
        totalCorrect += stats.correctCount;
        totalIncorrect += stats.incorrectCount;
      }
    });
    
    const totalAttempts = totalCorrect + totalIncorrect;
    const accuracy = totalAttempts > 0 ? (totalCorrect / totalAttempts) * 100 : 0;
    
    return {
      totalIntervals,
      masteredCount,
      accuracy: accuracy.toFixed(1)
    };
  }, [intervals]);

  // Get statistics summary
  const getStatsSummary = useMemo(() => {
    const totalNotes = 29; // Total notes reachable through the pedagogy unlock system
    let masteredCount = 0;
    let totalCorrect = 0;
    let totalIncorrect = 0;
    let avgResponseTime = 0;
    let responseCount = 0;
    
    // Count mastered notes from all notes we have stats for
    Object.keys(notes).forEach(note => {
      const stats = notes[note];
      if (stats) {
        if (stats.masteryTier >= 3) masteredCount++;
        totalCorrect += stats.correctCount;
        totalIncorrect += stats.incorrectCount;
        if (stats.responseTimes) {
          responseCount += stats.responseTimes.length;
          avgResponseTime += stats.responseTimes.reduce((a, b) => a + b, 0);
        }
      }
    });
    
    avgResponseTime = responseCount > 0 ? avgResponseTime / responseCount : 0;
    const totalAttempts = totalCorrect + totalIncorrect;
    const accuracy = totalAttempts > 0 ? (totalCorrect / totalAttempts) * 100 : 0;
    
    return {
      totalNotes,
      masteredCount,
      accuracy: accuracy.toFixed(1),
      avgResponseTime: Math.round(avgResponseTime),
      totalSessions: sessions.length,
      currentStreak: streak.currentCount
    };
  }, [notes, sessions.length, streak.currentCount]);

  // Get challenging notes (lowest accuracy)
  const getChallengingNotes = useCallback((limit = 5) => {
    const noteAccuracies = currentRange.unlockedNotes.map(note => {
      const stats = notes[note];
      if (!stats) return { note, accuracy: 100, attempts: 0 };
      
      const total = stats.correctCount + stats.incorrectCount;
      const accuracy = total > 0 ? (stats.correctCount / total) * 100 : 100;
      
      return { note, accuracy, attempts: total };
    });
    
    return noteAccuracies
      .filter(n => n.attempts >= 2)
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, limit);
  }, [notes, currentRange.unlockedNotes]);

  const value = useMemo(() => ({
    // State
    notes,
    intervals,
    sessions,
    streak,
    settings,
    currentRange,
    onboardingComplete,
    
    // Actions
    updateNoteStats,
    updateIntervalStats,
    saveSession,
    checkRangeExpansion,
    unlockNewNote,
    updateSettings,
    completeOnboarding,
    getMasteredCount,
    
    // Computed
    getStatsSummary,
    getIntervalStatsSummary,
    getChallengingNotes
  }), [
    notes, intervals, sessions, streak, settings, currentRange, onboardingComplete,
    updateNoteStats, updateIntervalStats, saveSession, checkRangeExpansion, unlockNewNote, updateSettings, completeOnboarding, getMasteredCount,
    getStatsSummary, getIntervalStatsSummary, getChallengingNotes
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export default AppContext;
